import React, { useEffect, useRef, useState } from 'react';
import { Popup } from './Popup';
import './Donateform.css';
import { useNavigate } from 'react-router-dom';
import MasterCardImage from '../../assets/PNG/mastercard.png'
import VisaCardImage from '../../assets/PNG/visa.png'
import AmericanExpressCardImage from '../../assets/PNG/american-express.png'
import DiscoverCardImage from '../../assets/PNG/discover-card-logo.jpg'
import AuthorizeNetImage from '../../assets/PNG/authorize-net-seeklogo.png'
import ENV from '../../constants/env-constants';
import { getUserDetails } from '../../utils/api';

// Cloudflare Turnstile (bot check against card testing). The site key is public; the secret key is only on the server.
const TURNSTILE_SITE_KEY = '0x4AAAAAAFmiVJ4fDs7UW7lP';
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Authorize.Net Accept.js: the card goes from the browser straight to Authorize.Net, which returns a
// one-time token (nonce). Our server only ever sees the token, never the card number or CVV.
const ACCEPT_SCRIPT = 'https://js.authorize.net/v1/Accept.js';
// Verified Merchant Seal (customer ID from the Authorize.Net Merchant Interface; public)
const ANET_SEAL_URL = 'https://verify.authorize.net/anetseal/';
const ANET_SEAL_ID = 'a37a208c-9559-4433-8d17-412fb2dfeba4';

function loadAccept() {
  if (window.Accept) return Promise.resolve(window.Accept);
  if (!window.__acceptLoading) {
    window.__acceptLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = ACCEPT_SCRIPT;
      s.charset = 'utf-8';
      s.onload = () => resolve(window.Accept);
      s.onerror = () => { window.__acceptLoading = null; reject(new Error('Accept.js did not load')); };
      document.head.appendChild(s);
    });
  }
  return window.__acceptLoading;
}

// Each token can be used once, so a monthly donation asks for two: today's charge and the subscription
async function getCardToken(cardData) {
  const Accept = await loadAccept();
  const authData = { clientKey: ENV.AUTHNET_CLIENT_KEY, apiLoginID: ENV.AUTHNET_LOGIN_ID };
  const response = await new Promise((resolve) => Accept.dispatchData({ authData, cardData }, resolve));
  if (response.messages.resultCode === 'Error') {
    throw new Error(response.messages.message.map((m) => m.text).join(' '));
  }
  return response.opaqueData;
}

function loadTurnstile() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (!window.__turnstileLoading) {
    window.__turnstileLoading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = TURNSTILE_SCRIPT;
      s.async = true;
      s.onload = () => resolve(window.turnstile);
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  return window.__turnstileLoading;
}

function Donateform({ userId }) {
  const [selectedFrequency, setSelectedFrequency] = useState('monthly');
  const [selectedAmount, setSelectedAmount] = useState('$25');
  const [customAmount, setCustomAmount] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryMonth, setExpiryMonth] = useState('');
  const [expiryYear, setExpiryYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [companyOrOrganizationName, setCompanyOrOrganizationName] = useState('');
  const [countryName, setCountryName] = useState('');
  const [userType, setUserType] = useState('');
  const [zipcode, setZipCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  // popup
  const [showPopup, setShowPopup] = useState(false);
  const [transactionMessage, setTransactionMessage] = useState('');
  // Turnstile
  const turnstileBox = useRef(null);
  const turnstileId = useRef(null);
  const [turnstileToken, setTurnstileToken] = useState('');

  useEffect(() => {
    let cancelled = false;
    loadTurnstile().then((ts) => {
      if (cancelled || !turnstileBox.current || turnstileId.current !== null) return;
      turnstileId.current = ts.render(turnstileBox.current, {
        sitekey: TURNSTILE_SITE_KEY,
        action: 'donate',
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken(''),
      });
    }).catch(() => setErrorMessage('The security check could not load. Please refresh the page.'));
    return () => {
      cancelled = true;
      if (window.turnstile && turnstileId.current !== null) window.turnstile.remove(turnstileId.current);
      turnstileId.current = null;
    };
  }, []);

  // Each token works once, so get a fresh one after every attempt
  const resetTurnstile = () => {
    setTurnstileToken('');
    if (window.turnstile && turnstileId.current !== null) window.turnstile.reset(turnstileId.current);
  };

  useEffect(()=>{
    async function storeDetails() {
      const data = await getUserDetails(userId);
      setFirstName(data.firstName);
      setLastName(data.lastName);
      setCompanyOrOrganizationName(data.companyOrOrganization);
      setCountryName(data.country);
      setEmail(data.email);
      setZipCode(data.zipcode)
      setUserType(data.userType)
    }
    if (userId) storeDetails()
  }, []);
  const validateForm = () => {
    // Ensure that either a preset amount is selected or a custom amount is entered.
    const amount = selectedAmount === 'custom' ? customAmount : selectedAmount;
    if (!selectedFrequency || (!selectedAmount && !customAmount) || !cardNumber || !expiryMonth || !expiryYear || !cvv) {
      setErrorMessage('Please fill all fields.');
      return false;
    }
    if (!turnstileToken) {
      setErrorMessage('Please complete the security check above the Submit button.');
      return false;
    }
    setErrorMessage('');
    return true;
  };

  const handleFrequencySelect = (frequency) => {
    setSelectedFrequency(frequency);
  };

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    // If a preset amount is selected, clear the custom amount.
    if (amount !== 'custom') {
      setCustomAmount('');
    }
  };


  const handleCustomAmountChange = (event) => {
    const value = event.target.value;
    // Directly set custom amount from the event, and ensure 'custom' is selected.
    setCustomAmount(value);
    setSelectedAmount('custom');
  };


  const handleNumericChange = (event, setter) => {
    const value = event.target.value;
    if (/^\d*$/.test(value)) { // Allows only numbers or an empty string
      setter(value);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return; // Prevent form submission if validation fails
    }
    
    const parsedAmount = selectedAmount === 'custom' ? parseFloat(customAmount) : parseFloat(selectedAmount.replace('$', ''));
    
    setShowPopup(true); // Show loading indicator (considered as part of popup for simplicity)
    setTransactionMessage('Processing...');

    let opaqueData, subscriptionOpaqueData;
    try {
      const cardData = { cardNumber, month: expiryMonth, year: String(expiryYear), cardCode: cvv, zip: zipcode, fullName: `${firstName} ${lastName}`.trim() };
      opaqueData = await getCardToken(cardData);
      if (selectedFrequency === 'monthly') subscriptionOpaqueData = await getCardToken(cardData);
    } catch (error) {
      resetTurnstile();
      setTransactionMessage('Transaction Failed: ' + (error.message || 'please check the card details.'));
      return;
    }

    const payload = {
      userid: userId,
      opaqueData,
      subscriptionOpaqueData,
      amount: isNaN(parsedAmount) ? 0 : parsedAmount,
      currencyCode: "USD",
      frequency: selectedFrequency,
      firstName,
      lastName,
      email,
      companyOrOrganizationName,
      countryName,
      zipcode,
      userType,
      turnstileToken
    };

  // Perform the API call
  fetch(`${ENV.API_URL}/api/donate/makePayment`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  .then(response => response.json())
  .then(data => {
    resetTurnstile();
    // Check if the transaction was successful or failed based on API response
    if (data.success) {
    navigate('/site/donate/confirm');
    } else {
      setTransactionMessage(data.message || 'Transaction Failed'); // Show API provided message or a default one
    }
  })
  .catch(error => {
    resetTurnstile();
    console.error('Error:', error);
    setTransactionMessage('An error occurred. Please try again.'); // Handle any fetch errors
  });
  };


  // Utility function to dynamically apply selection styles
  const isSelected = (value) => {
    return selectedFrequency === value || selectedAmount === value ? 'selected' : '';
  };

  return (
    <div id="right-side"  style={{display:'flex', justifyContent:'center', alignItems:'center'}} className="col-lg-6">
      <form id="payment-form" onSubmit={handleSubmit}>
        <h1 style={{ color: 'var(--dark)' }}>Help us make a difference!</h1>
        {/* Donation Frequency */}
        <div className="frequency-buttons">
          <div className={`box ${isSelected('one-time')}`} onClick={() => handleFrequencySelect('one-time')}>One Time</div>
          <div className={`box ${isSelected('monthly')}`} onClick={() => handleFrequencySelect('monthly')}>Monthly</div>
        </div>
        {/* Donation Amount */}
        <h3 style={{ marginTop: '10px' }}>Select Donation Amount</h3>
        <div className="donation-amount">
          <div className={`block ${isSelected('$5')}`} onClick={() => handleAmountSelect('$5')}>$5</div>
          <div className={`block ${isSelected('$25')}`} onClick={() => handleAmountSelect('$25')}>$25</div>
          <div className={`block ${isSelected('$100')}`} onClick={() => handleAmountSelect('$100')}>$100</div>
          <div className={`block ${isSelected('$500')}`} onClick={() => handleAmountSelect('$500')}>$500</div>
          <input type="text" className={`block ${isSelected('custom')}`} placeholder="Custom Amount" value={customAmount} inputMode="numeric" pattern="\d*" onChange={(e) => {handleNumericChange(e, setCustomAmount);handleCustomAmountChange(e)}} />
        </div>
        {/* Payment Options */}
        <h2 style={{ marginTop: '15px' }}>Payment Options</h2>
        <div id="payment-options">
          <div style={{ marginBottom: '3%' }} className="payment-option">
            {/* <img src="/img/img/card1.png" width={80} height={30} alt="" /> */}
            <img src={VisaCardImage} width={70} alt="visa-card-log" />
            <img src={MasterCardImage} style={{marginRight: "0.5rem"}} width={50} alt="master-card-logo" />
            <img src={AuthorizeNetImage} style={{marginRight: "0.5rem"}} width={70} alt="authorize-net-logo" />
            <img src={AmericanExpressCardImage} width={45} alt="american-express-logo" />
            <img src={DiscoverCardImage} width={80} alt="discover-card-logo" />
          </div>
        </div>
        <div style={{display: "flex", columnGap: '1rem'}}>
          <input type="text" placeholder='First Name' className="credit-card-inputs" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
          <input type="text" placeholder='Last Name' className="credit-card-inputs" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
        </div>
        <div>
          <input type="text" placeholder='Email Id' className="credit-card-inputs" value={email} onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div>
          <input type="text" placeholder='Company or Organization (if applicable)' className="credit-card-inputs" value={companyOrOrganizationName} onChange={(e) => setCompanyOrOrganizationName(e.target.value)} />
        </div>
        <div style={{display: "flex", columnGap: '1rem'}}>
                <select value={countryName} onChange={(e) => setCountryName(e.target.value)} className="credit-card-inputs" aria-label="Country">
                  <option value="" disabled>-- Please Choose The Country --</option>
                  <option>United States</option>
                  <option>Australia</option>
                  <option>Canada</option>
                  <option>New Zealand</option>
                  <option>South Africa</option>
                  <option>United Kingdom</option>
                </select>
        </div>
        <div style={{display: "flex", columnGap: '1rem'}}>
                <select value={userType} onChange={(e) => setUserType(e.target.value)} className="credit-card-inputs" aria-label="User type" required>
                  <option value="" disabled>-- Please Choose The Category --</option>
                  <option value="individual">Individuals</option>
                  <option value="corporate">Corporate Partners</option>
                  <option value="educational">Educational Institutions</option>
                  <option value="foundations">Foundations and Trusts</option>
                </select>
        </div>
        <div style={{display: "flex", columnGap: '1rem'}}>
        <input type="text" placeholder='Zipcode' className="credit-card-inputs" value={zipcode} onChange={(e) => setZipCode(e.target.value)} required />
        </div>
        <input type="text" placeholder="Debit or Credit Card Number" className="credit-card-inputs" value={cardNumber} inputMode="numeric" pattern="\d*" onChange={(e) => handleNumericChange(e, setCardNumber)} />
        <div className="expiration-date">
          <select id="expiry-month" value={expiryMonth} onChange={(e) => setExpiryMonth(e.target.value)}>
            <option value="" disabled>Month</option>
            {/* Months */}
            {[...Array(12)].map((_, i) => (
              <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{String(i + 1).padStart(2, '0')} - {new Date(0, i).toLocaleString('en', { month: 'long' })}</option>
            ))}
          </select>
          <select id="expiry-year" value={expiryYear} onChange={(e) => setExpiryYear(e.target.value)}>
            <option value="" disabled>Year</option>
            {/* Years */}
            {[...Array(10)].map((_, i) => (
              <option key={i} value={new Date().getFullYear() + i}>{new Date().getFullYear() + i}</option>
            ))}
          </select>
          <input type="text" placeholder="CVV" inputMode="numeric" pattern="\d*" onChange={(e) => handleNumericChange(e, setCvv)} className="cvv-input" style={{ marginTop: '-1px', height: '40px' }} value={cvv} />
        </div>
        <div ref={turnstileBox} style={{ margin: '12px 0', minHeight: '65px' }} />
        <button type="submit" className="submit-button btn btn-primary btn-lg">Submit Donation</button>
        {/* Error Message Display */}
        {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
        {/* Authorize.Net Verified Merchant Seal (same link and image as Authorize.Net's seal.js, which cannot run inside React) */}
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <a href={`${ANET_SEAL_URL}?pid=${ANET_SEAL_ID}&rurl=${encodeURI(window.location.protocol + '//' + window.location.hostname)}`}
             target="_blank" rel="noopener noreferrer" title="Authorize.Net Merchant - Click to Verify">
            <img src={`${ANET_SEAL_URL}images/secure90x72.gif`} width="90" height="72" alt="Authorize.Net Merchant - Click to Verify" style={{ border: 0 }} />
          </a>
        </div>
      </form>
      {/* popup */}
      {showPopup && <Popup message={transactionMessage} onClose={() => setShowPopup(false)} />}
    </div>
  );
}

export default Donateform;
